import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { concat, forkJoin, Observable, of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, withLatestFrom } from 'rxjs/operators';
import { notification } from '../../components';
import { ErrorMsg, getErrorOrFallbackMessage, notifyErrorOrFallback } from '../../settings/models/Error';
import { commandRequest } from '../../helpers/request/utils';
import { Action, UUID } from '../app/basicModels';
import { getPhenomenonsTreeByCompany, getPhenomenonsTreeByWorkspace } from '../company/service';
import { getPhenomenaByMethodology } from '../methodology/services';
import methodologyDeepActions from '../methodologyDeep/actions';
import methodologyClusterActions from '../methodologyCluster/actions';
import { ComponentDeep, MethodologyDeep, PhenomenonDeep } from '../methodologyDeep/models';
import actions from './actions';
import {
  CloneCharacteristicsIndicatorsDTO,
  ClonedCharacteristicsIndicators,
  LoadPhenomenonQueryPayload,
  LoadPhenomenonsTreeByWorkspacePayload,
  Phenomenon,
  PhenomenonPage,
  PhenomenonSingle
} from './models';
import {
  cloneCharacteristicsIndicators,
  deletePhenomenon,
  getPhenomenon,
  getPhenomenons,
  getPhenomenonsSimple,
  savePhenomenon
} from './service';
import {
  fixPhenomenonPayload,
  getComponentsToAddFromClonedPhenomenon,
  getOriginalKey,
  parsePhenomenonSingleToPhenomenon,
  updatePhenomenaTree,
  upsertPhenomenonInMethodologyDeep
} from './utils';
import { chunk, cloneDeep, isEmpty } from 'lodash';
import * as Sentry from '@sentry/react';
import { AppState } from '../redux.model';
import { User } from '../models';
import { getPhenomenaDeepFromMethodologyDeep, insertComponentsOnMethodologyDeep } from '../methodologyDeep/services';
import { applyWorkspaceIdToPayload } from '../methodologyDeep/scope';
import { trackingEvent } from '../../helpers/useTrackingEvents';
import { getValueLanguage } from '../language/utils';
import { MethodologyCluster } from '../methodologyCluster/models';
import { uuid } from '../../helpers';
import { CommandRequest } from '../../helpers/request/models';

export const handleLoadPhenomenons = action$ =>
  action$.pipe(
    ofType(actions.LOAD_PHENOMENONS),
    map((action: Action<LoadPhenomenonQueryPayload>) => action.payload),
    exhaustMap((payload: LoadPhenomenonQueryPayload) =>
      getPhenomenons(payload.pageableQuery, payload.companyId, payload.showCanonicals).pipe(
        map(response => response.data),
        map((phenomenons: PhenomenonPage) => {
          return actions.loadPhenomenonsSuccess(phenomenons, payload.companyId);
        }),
        catchError((error: ErrorMsg) => {
          const errorMsg = notifyErrorOrFallback(error, 'Error loading phenomenons!');
          return of(actions.loadPhenomenonsFailure(errorMsg));
        })
      )
    )
  );

export const handleLoadPhenomenonsByMethodology = action$ =>
  action$.pipe(
    ofType(actions.LOAD_PHENOMENONS_BY_METHODOLOGY),
    map((action: Action<LoadPhenomenonQueryPayload>) => action.payload),
    concatMap((payload: any) =>
      getPhenomenaByMethodology(payload).pipe(
        map(response => response.data),
        map((phenomenons: PhenomenonSingle[]) => {
          const phenonomenonsSigle = phenomenons.map(p => parsePhenomenonSingleToPhenomenon(p) as PhenomenonSingle);
          return actions.loadPhenomenonsByMethodologySuccess(phenonomenonsSigle);
        }),
        catchError((error: ErrorMsg) => {
          const errorMsg = notifyErrorOrFallback(error, 'Error loading phenomenons by methodology!');
          return of(actions.loadPhenomenonsByMethodologyFailure(errorMsg));
        })
      )
    )
  );

export const handleLoadPhenomenonsSimple = action$ =>
  action$.pipe(
    ofType(actions.LOAD_PHENOMENONS_SIMPLE),
    map((action: Action<Array<UUID>>) => action.payload),
    concatMap((ids: UUID[]) => {
      return forkJoin(chunk(ids, 500).map(chunkIds => getPhenomenonsSimple(chunkIds).pipe(map(response => response.data)))).pipe(
        map(result => result.flat()),
        map(phenomenons => {
          return actions.loadPhenomenonsSimpleSuccess(phenomenons);
        }),
        catchError((error: ErrorMsg) => {
          const errorMsg = notifyErrorOrFallback(error, 'Error loading phenomenons simple!');
          return of(actions.loadPhenomenonsSimpleFailure(errorMsg));
        })
      );
    })
  );

export const handleLoadPhenomenon = action$ =>
  action$.pipe(
    ofType(actions.LOAD_PHENOMENON),
    map((action: Action<UUID>) => action.payload),
    concatMap((phenomenonId: UUID) =>
      getPhenomenon(phenomenonId).pipe(
        map(response => response.data),
        map(phenomenon => {
          return actions.loadPhenomenonSuccess(phenomenon);
        }),
        catchError((error: ErrorMsg) => {
          const errorMsg = notifyErrorOrFallback(error, 'Error loading phenomenon!');
          return of(actions.loadPhenomenonFailure(errorMsg));
        })
      )
    )
  );

export const handleSavePhenomenon = (
  action$: ActionsObservable<ReturnType<typeof actions.savePhenomenon> & { metadata?: Record<string, unknown> }>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(actions.SAVE_PHENOMENON),
    map((action: ReturnType<typeof actions.savePhenomenon> & { metadata?: Record<string, unknown> }) => action),
    withLatestFrom(
      state$.pipe(
        map((state: AppState) => ({
          methodologySelect: state.Methodology.methodologySelect,
          currentUser: state.User.currentUser as User,
          phenomenonCategories: state.PhenomenonCategory.entities,
          methodologyDeep: state.MethodologyDeep.methodologyDeep,
          phenomenonsTree: state.Phenomenon.phenomenonsTree
        }))
      )
    ),
    map(([action, { methodologySelect, currentUser, phenomenonCategories, methodologyDeep, phenomenonsTree }]) => {
      const { phenomenon } = action.payload;
      // Only inherit the workspace scope from the open methodology when the save actually originates
      // from a methodology context (methodologyId present). Standalone canonical/owned-phenomenon saves
      // must not pick up a stale methodologyDeep.workspace_id: a new Reference Canonical would otherwise
      // be sent with a workspace_id and lose its is_canonical flag (P40-59589).
      const methodologyWorkspaceId = action.payload.methodologyId ? methodologyDeep?.workspace_id : undefined;
      const phenomenonToSave = commandRequest(fixPhenomenonPayload(phenomenon, methodologyWorkspaceId), currentUser);
      if (phenomenon.isNew) {
        if (methodologySelect && methodologySelect.company_id && !phenomenonToSave.payload.company_id) {
          phenomenonToSave.payload.company_id = methodologySelect.company_id;
        }
      }
      phenomenonToSave.payload = applyWorkspaceIdToPayload(phenomenonToSave.payload, methodologyWorkspaceId);
      return {
        ...action,
        payload: phenomenonToSave,
        methodologyId: action.payload.methodologyId,
        orderedPhenomenonId: action.payload.orderedPhenomenonId,
        orderedInspectionGroupId: action.payload.orderedInspectionGroupId,
        phenomenonCategories,
        methodologyDeep,
        phenomenonsTree
      };
    }),
    exhaustMap(action => {
      const { orderedPhenomenonId, orderedInspectionGroupId } = action;
      const phenomenon = action.payload;
      const { phenomenonCategories } = action;
      const { methodologyDeep } = action;
      const phenomenaTree: Phenomenon[] = action.phenomenonsTree;

      if (isString(phenomenon.payload.category)) {
        phenomenon.payload.category = phenomenonCategories[phenomenon.payload.category];
      }
      const { isNew } = phenomenon.payload;
      return savePhenomenon(phenomenon, isNew).pipe(
        concatMap(_ => {
          if (action.methodologyId && methodologyDeep) {
            const mutableMethodologyDeep: MethodologyDeep = upsertPhenomenonInMethodologyDeep(
              methodologyDeep,
              phenomenon.payload,
              orderedPhenomenonId,
              orderedInspectionGroupId,
              phenomenaTree
            );

            const mutablePhenomenaTree: Phenomenon[] = updatePhenomenaTree(phenomenon.payload, phenomenaTree);

            return concat([
              actions.savePhenomenonSuccess(phenomenon.payload),
              methodologyDeepActions.updateMethodologyDeep(mutableMethodologyDeep),
              actions.updatePhenomenonTree(mutablePhenomenaTree)
            ]);
          }
          return of(actions.savePhenomenonSuccess(phenomenon.payload));
        }),
        catchError((error: any) => {
          const fixedMessage = 'Phenomenon could not be updated';
          const processedMessage = notifyErrorOrFallback(error, fixedMessage);

          // this console is proposal for try capture this error.
          // eslint-disable-next-line no-console
          console.error({
            action,
            phenomenonCategories,
            methodologyDeep,
            fixedMessage,
            error,
            phenomenon,
            processedMessage
          });
          Sentry.captureException(JSON.stringify({ fixedMessage, error, phenomenon, processedMessage }));

          return of(actions.savePhenomenonFailure(processedMessage));
        })
      );
    })
  );

export const handleDeletePhenomenon = (action$, state$) =>
  action$.pipe(
    ofType(actions.DELETE_PHENOMENON),
    map((action: Action<Phenomenon>) => action.payload),
    withLatestFrom(state$.pipe(map((state: any) => state.User.currentUser))),
    map(([phenomenon, currentUser]: any) => commandRequest(phenomenon, currentUser)),
    withLatestFrom(state$.pipe(map((state: AppState) => state.Phenomenon.entities))),
    concatMap(([phenomenon, phenomenons]) => {
      if (!phenomenon.payload.company_id) {
        phenomenon.payload = phenomenons[phenomenon.payload.id];
      }
      return deletePhenomenon(phenomenon).pipe(
        map(response => {
          notification('success', 'Phenomenon deleted!');
          return actions.deletePhenomenonSuccess(phenomenon.payload);
        }),
        catchError((error: any) => {
          const msg = notifyErrorOrFallback(error, 'Error deleting phenomenon!');
          return of(actions.deletePhenomenonFailure(msg));
        })
      );
    })
  );

export const handleLoadPhenomenonsTree = (action$: ActionsObservable<Action<LoadPhenomenonQueryPayload>>) =>
  action$.pipe(
    ofType(actions.LOAD_PHENOMENONS_TREE),
    map((action: Action<LoadPhenomenonQueryPayload>) => action.payload),
    concatMap((payload: LoadPhenomenonQueryPayload) =>
      getPhenomenonsTreeByCompany(payload.pageableQuery, payload.companyId).pipe(
        map(response => response.data),
        map((phenomenonsTreePage: PhenomenonPage) => actions.loadPhenomenonsTreeSuccess(phenomenonsTreePage.content)),
        catchError((error: ErrorMsg) => {
          const errorMsg = notifyErrorOrFallback(error, 'Error loading phenomenons!');
          return of(actions.loadPhenomenonsTreeFailure(errorMsg));
        })
      )
    )
  );

export const handleLoadPhenomenonsTreeByWorkspace = (action$: ActionsObservable<Action<LoadPhenomenonsTreeByWorkspacePayload>>) =>
  action$.pipe(
    ofType(actions.LOAD_PHENOMENONS_TREE_BY_WORKSPACE),
    map((action: Action<LoadPhenomenonsTreeByWorkspacePayload>) => action.payload),
    concatMap((payload: LoadPhenomenonsTreeByWorkspacePayload) =>
      getPhenomenonsTreeByWorkspace(payload.pageableQuery, payload.workspaceId).pipe(
        map(response => response.data),
        map((phenomenonsTreePage: PhenomenonPage) => actions.loadPhenomenonsTreeSuccess(phenomenonsTreePage.content)),
        catchError((error: ErrorMsg) => {
          const errorMsg = notifyErrorOrFallback(error, 'Error loading phenomenons!');
          return of(actions.loadPhenomenonsTreeFailure(errorMsg));
        })
      )
    )
  );

export const handleSavePhenomenonSuccess = (action$, state$) =>
  action$.pipe(
    ofType(actions.SAVE_PHENOMENON_SUCCESS),
    withLatestFrom(state$.pipe(map((state: any) => state.Phenomenon.editModalActive))),
    concatMap(([{ payload }, editModalActive]): Observable<any> => {
      notification('success', `${payload.isNew ? 'Save' : 'Update'} Phenomenon Success!`);
      if (editModalActive) {
        return concat([actions.togglePhenomenonEditModal(), actions.unselectPhenomenon()]);
      }
      return of();
    })
  );

export const handleCloneCharacteristicIndicatorToPhenomenon = (
  action$: ActionsObservable<Action<CloneCharacteristicsIndicatorsDTO[]>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(actions.CLONE_CHARACTERISTICS_INDICATORS_TO_PHENOMENON),
    map((action: Action<CloneCharacteristicsIndicatorsDTO[]>) => action.payload ?? ({} as CloneCharacteristicsIndicatorsDTO[])),
    withLatestFrom(
      state$.pipe(
        map(state => ({
          currentUser: state.User.currentUser as User,
          methodologyDeep: state.MethodologyDeep.methodologyDeep as MethodologyDeep,
          methodologyCluster: state.MethodologyCluster.component_clusters
        }))
      )
    ),
    concatMap(([payload, state]) => {
      const clonedPhenomenonMap = new Map<string, string>(
        payload.map(item => [item.destiny_phenomenon_id, item.destiny_ordered_phenomenon_id])
      );
      const workspaceId = state.methodologyDeep?.workspace_id;
      const itemsToClone = payload.map(item => {
        const wrapped = commandRequest(item, state.currentUser);
        wrapped.payload = applyWorkspaceIdToPayload(wrapped.payload, workspaceId);
        return wrapped;
      });

      return forkCloneRequests(itemsToClone).pipe(
        concatMap(response => handleCloneResponse(response, state, clonedPhenomenonMap)),
        catchError((error: ErrorMsg) => handleCloneError(error))
      );
    })
  );

function isString(value) {
  return typeof value === 'string' || value instanceof String;
}

function getErrorMessage(error: ErrorMsg) {
  let errorMsg = error?.response?.data?.detail as string;
  if (errorMsg) {
    return errorMsg;
  }

  errorMsg = getErrorOrFallbackMessage(error, 'Error cloning characteristics and indicators!');
  return errorMsg;
}

export const handleCloneCluster = (
  clonedItems: ClonedCharacteristicsIndicators[],
  methodologyCluster: MethodologyCluster[],
  clonedPhenomenonMap: Map<string, string>
): MethodologyCluster[] => {
  if (!clonedItems || clonedItems.length === 0) {
    return [];
  }
  const clonedClusters: MethodologyCluster[] = [];

  clonedItems.forEach(clonedItem => {
    const newPhenomenonId = clonedItem.destiny_phenomenon_id;
    const orderedPhenomenonId = clonedPhenomenonMap.get(newPhenomenonId);
    const characteristicsIdMap = new Map(Object.entries(clonedItem.characteristic_ids_map || {}));
    const usedComponentIds = new Set(
      [...methodologyCluster, ...clonedClusters]
        .filter(
          cluster =>
            cluster.phenomenon_id === newPhenomenonId &&
            (!orderedPhenomenonId || !cluster.phenomenon_unique_id || cluster.phenomenon_unique_id === orderedPhenomenonId)
        )
        .flatMap(cluster => cluster.components_ids)
    );
    const originalClusters = methodologyCluster.filter(cluster => cluster.phenomenon_id === clonedItem.reference_phenomenon_id);

    originalClusters.forEach(originalCluster => {
      const mappedComponentIds = originalCluster.components_ids.map(oldId => characteristicsIdMap.get(oldId)).filter(Boolean) as string[];
      const newComponentsIds = [...new Set(mappedComponentIds)].filter(id => !usedComponentIds.has(id));

      if (newComponentsIds.length > 0) {
        const clonedCluster: MethodologyCluster = {
          ...originalCluster,
          id: uuid(),
          phenomenon_id: newPhenomenonId,
          phenomenon_unique_id: orderedPhenomenonId,
          components_ids: newComponentsIds
        };

        clonedClusters.push(clonedCluster);
        newComponentsIds.forEach(id => usedComponentIds.add(id));
      }
    });
  });

  return clonedClusters;
};

type CloneRequest = CommandRequest<CloneCharacteristicsIndicatorsDTO>;

type CloneResponseItem = string | { data: ClonedCharacteristicsIndicators };

const forkCloneRequests = (itemsToClone: CloneRequest[]) => {
  return cloneCharacteristicsIndicators(itemsToClone[0]).pipe(
    concatMap(response => {
      const remainingItems = itemsToClone.slice(1);
      const remainingRequests = remainingItems.map(item =>
        cloneCharacteristicsIndicators(item).pipe(catchError((error: ErrorMsg) => of(getErrorMessage(error))))
      );
      return forkJoin([of(response), ...remainingRequests]);
    })
  );
};

const handleCloneResponse = (
  response: CloneResponseItem[],
  state: { currentUser: User; methodologyDeep: MethodologyDeep; methodologyCluster: MethodologyCluster[] },
  clonedPhenomenonMap: Map<string, string>
) => {
  let mutableMethodologyDeep = cloneDeep(state.methodologyDeep);
  let clonedVariables = 0;
  let clonedIndicators = 0;
  let clonedPhenomena = 0;

  const { clonedItems, errors } = collectClonedItems(response);
  const phenomenaDeep: PhenomenonDeep[] = clonedItems.length ? getPhenomenaDeepFromMethodologyDeep(state.methodologyDeep) : [];
  const globalPriorityMap = buildGlobalComponentPriorityMap(phenomenaDeep);

  for (const clonedItem of clonedItems) {
    if (!isEmpty(clonedItem.characteristics) || !isEmpty(clonedItem.indicators)) {
      const { characteristics, indicators } = clonedItem;
      const phenomenon: Partial<Phenomenon> = {
        id: clonedItem.destiny_phenomenon_id,
        characteristics,
        indicators
      };

      clonedVariables += characteristics.length;
      clonedIndicators += indicators.length;
      clonedPhenomena += 1;

      const orderedPhenomenonId = clonedPhenomenonMap.get(phenomenon.id as string) as string;

      const destinationPhenomenon = phenomenaDeep.find(p => p.phenomenonId === phenomenon.id);
      const destinationPhenomenonComponents = destinationPhenomenon?.components ?? [];

      const sourcePhenomenon = phenomenaDeep.find(p => p.phenomenonId === clonedItem.reference_phenomenon_id);
      const sourcePhenomenonComponents = sourcePhenomenon?.components ?? [];

      const componentsToAddFiltered = getComponentsToAddFromClonedPhenomenon(phenomenon, destinationPhenomenonComponents);

      const characteristicReverseMap = new Map<string, string>();
      const indicatorReverseMap = new Map<string, string>();
      for (const [origin, dest] of Object.entries(clonedItem.characteristic_ids_map || {})) {
        characteristicReverseMap.set(dest, origin);
      }
      for (const [origin, dest] of Object.entries(clonedItem.indicator_ids_map || {})) {
        indicatorReverseMap.set(dest, origin);
      }

      const sourceOrderMap = buildSourceOrderMap(sourcePhenomenonComponents);
      mergeMissingSourcePriorities(sourceOrderMap, globalPriorityMap, characteristicReverseMap, indicatorReverseMap);
      const characteristicComponentsToAdd = componentsToAddFiltered.filter(component => component.characteristic);
      const indicatorComponentsToAdd = componentsToAddFiltered.filter(component => !component.characteristic);
      const sortedCharacteristicComponents = sortComponentsBySourcePriority(characteristicComponentsToAdd, {
        sourceOrderMap,
        characteristicReverseMap,
        indicatorReverseMap
      });
      const sortedComponentsToAdd = [...sortedCharacteristicComponents, ...indicatorComponentsToAdd];

      mutableMethodologyDeep = insertComponentsOnMethodologyDeep(mutableMethodologyDeep, orderedPhenomenonId, sortedComponentsToAdd);
    }
  }

  if (errors.length) {
    // eslint-disable-next-line no-console
    console.log('Errors cloning characteristics and indicators', errors);
  }

  const errorNotification = errors.length ? `, and error cloning ${errors.length} phenomena` : '';
  const notificationStatus = errors.length ? 'warning' : 'success';
  const notificationMessage = `${clonedItems.length} phenomena cloned with success${errorNotification}!`;
  notification(notificationStatus, notificationMessage);

  const { id: userId, job_title: jobTitle, name: userName } = state.currentUser;
  const { id: methodologyId, crop, company } = mutableMethodologyDeep;

  trackingEvent('Cloned Variables and Indicators', {
    userId,
    jobTitle,
    userName,
    methodologyId,
    cropId: crop?.id,
    cropName: getValueLanguage(crop?.name),
    organizationId: company?.id,
    organizationName: company?.name,
    clonedVariables,
    clonedIndicators,
    clonedPhenomena
  });

  const newClusters = handleCloneCluster(clonedItems, state.methodologyCluster, clonedPhenomenonMap);

  return concat([
    methodologyDeepActions.addCharacteristicIndicatorsSuccess(mutableMethodologyDeep),
    actions.cloneCharacteristicsIndicatorsToPhenomenonSuccess(clonedItems, errors),
    methodologyDeepActions.updateClonedPhenomenaTotals(clonedVariables, clonedIndicators, clonedPhenomena),
    methodologyDeepActions.toggleCloneCharacteristicIndicatorDrawer(),
    methodologyClusterActions.addClonedClusters(newClusters)
  ]);
};

const collectClonedItems = (response: CloneResponseItem[]) => {
  const clonedItems: ClonedCharacteristicsIndicators[] = [];
  const errors: string[] = [];
  response.forEach(item => {
    if (typeof item === 'string') {
      errors.push(item);
    } else {
      clonedItems.push(item.data);
    }
  });
  return { clonedItems, errors };
};

const buildGlobalComponentPriorityMap = (components: PhenomenonDeep[]) => {
  const sourceOrderMap = new Map<string, number>();
  components.forEach(phenomenon => {
    (phenomenon.components ?? []).forEach((component, index) => {
      const priority = component?.characteristic?.priority ?? component?.indicator?.priority ?? index;
      if (component?.characteristic) {
        sourceOrderMap.set(`c:${component.characteristic.characteristicId}`, priority);
      }
      if (component?.indicator) {
        sourceOrderMap.set(`i:${component.indicator.indicatorId}`, priority);
      }
    });
  });
  return sourceOrderMap;
};

const buildSourceOrderMap = (components: ComponentDeep[]) => {
  const sourceOrderMap = new Map<string, number>();
  components.forEach((component, index) => {
    const priority = component?.characteristic?.priority ?? component?.indicator?.priority ?? index;
    if (component?.characteristic) {
      sourceOrderMap.set(`c:${component.characteristic.characteristicId}`, priority);
    }
    if (component?.indicator) {
      sourceOrderMap.set(`i:${component.indicator.indicatorId}`, priority);
    }
  });
  return sourceOrderMap;
};

const mergeMissingSourcePriorities = (
  sourceOrderMap: Map<string, number>,
  globalPriorityMap: Map<string, number>,
  characteristicReverseMap: Map<string, string>,
  indicatorReverseMap: Map<string, string>
) => {
  for (const originalId of characteristicReverseMap.values()) {
    const key = `c:${originalId}`;
    if (!sourceOrderMap.has(key) && globalPriorityMap.has(key)) {
      const priority = globalPriorityMap.get(key);
      if (priority !== undefined) {
        sourceOrderMap.set(key, priority);
      }
    }
  }
  for (const originalId of indicatorReverseMap.values()) {
    const key = `i:${originalId}`;
    if (!sourceOrderMap.has(key) && globalPriorityMap.has(key)) {
      const priority = globalPriorityMap.get(key);
      if (priority !== undefined) {
        sourceOrderMap.set(key, priority);
      }
    }
  }
};

interface SourcePriorityDependencies {
  sourceOrderMap: Map<string, number>;
  characteristicReverseMap: Map<string, string>;
  indicatorReverseMap: Map<string, string>;
}

const compareComponentsByPriority = (sourceOrderMap: Map<string, number>, characteristicReverseMap: Map<string, string>) => {
  return (first: ComponentDeep, second: ComponentDeep) => {
    const firstPriority = sourceOrderMap.get(getOriginalKey(first, characteristicReverseMap)) ?? Number.MAX_SAFE_INTEGER;
    const secondPriority = sourceOrderMap.get(getOriginalKey(second, characteristicReverseMap)) ?? Number.MAX_SAFE_INTEGER;
    return firstPriority - secondPriority;
  };
};

const sortComponentsBySourcePriority = (components: ComponentDeep[], dependencies: SourcePriorityDependencies) => {
  return [...components].sort(compareComponentsByPriority(dependencies.sourceOrderMap, dependencies.characteristicReverseMap));
};

const handleCloneError = (error: ErrorMsg) => {
  let errorMsg = error?.response?.data?.detail as string;
  if (errorMsg) {
    // eslint-disable-next-line no-console
    console.log('Errors cloning characteristics and indicators', errorMsg);
    notification('error', errorMsg);
  } else {
    errorMsg = notifyErrorOrFallback(error, 'Error cloning characteristics and indicators!');
  }
  return of(actions.cloneCharacteristicsIndicatorsToPhenomenonFailure([errorMsg]));
};
